import mapboxgl from "mapbox-gl"
import { stateToRegionByName } from "../../../../../constants/Map"
import { fNumber } from "../../../../../utils/formatNumber"
import { ICountyPublicWaterSystemResult, IRegionPublicWaterSystemResult, MapWaterSystemMetrics } from "../../../../../types"
import { ZOOM_LEVELS } from '../../../../../constants/Map'
import { Dispatch } from "redux"

let currentPopup: mapboxgl.Popup | null = null;

interface PopupConfig {
  title: string;
  subtitle: string;
  region?: string;
  violations: number;
  metrics?: {
    totalPopulation?: number;
    totalConnections?: number;
    totalViolations?: number;
    population?: number;
    connections?: number;
  };
  lngLat: mapboxgl.LngLat;
  map: mapboxgl.Map;
}

const createPopup = (config: PopupConfig): mapboxgl.Popup => {
  const { title, subtitle, region, violations, metrics, lngLat, map } = config;

  const popupContent = `
    <div class="map-popup">
      <div class="popup-header">
        <h3>${title} <span class="state-code">${subtitle}</span></h3>
      </div>
      <div class="popup-content">
        ${region ? `
          <div class="info-row">
            <span class="label">Region:</span>
            <span class="value">${region}</span>
          </div>
        ` : ''}
        <div class="info-row">
          <span class="label">Violations:</span>
          <span class="value ${violations > 0 ? 'violations-alert' : ''}">${fNumber(violations)}</span>
        </div>
        ${metrics ? `
          <div class="metrics-section">
            <div class="metrics-grid">
              <div class="metric-item">
                <span class="metric-value">${fNumber(metrics.totalPopulation ?? metrics.population ?? 0)}</span>
                <span class="metric-label">Population</span>
              </div>
              <div class="metric-item">
                <span class="metric-value">${fNumber(metrics.totalConnections ?? metrics.connections ?? 0)}</span>
                <span class="metric-label">Connections</span>
              </div>
              <div class="metric-item full-width">
                <span class="metric-value ${(metrics.totalViolations ?? violations ?? 0) > 0 ? 'violations-alert' : ''}">${fNumber(metrics.totalViolations ?? violations ?? 0)}</span>
                <span class="metric-label">Total Violations</span>
              </div>
            </div>
          </div>
        ` : ''}
      </div>
    </div>
  `;

  currentPopup?.remove();
  
  currentPopup = new mapboxgl.Popup({ 
    offset: 25,
    className: 'custom-popup',
    closeButton: false
  })
    .setLngLat(lngLat)
    .setHTML(popupContent)
    .addTo(map);

  currentPopup.once('close', () => {
    currentPopup = null;
  });

  return currentPopup;
};

export const handleHover = (
  e: mapboxgl.MapMouseEvent,
  mapRef?: React.RefObject<mapboxgl.Map>,
  regionsViolationsRef?: React.RefObject<IRegionPublicWaterSystemResult[]>,
  countyViolationsRef?: React.RefObject<ICountyPublicWaterSystemResult[]>,
  waterSystemMapMetricsRef?: React.RefObject<MapWaterSystemMetrics[]>,
  dispatch?: Dispatch
): mapboxgl.Popup | null => {
  if (!e.features?.length || !mapRef?.current || !dispatch) {
    return null;
  }
  
  const geographicName = e.features[0].properties?.name;
  if (!geographicName) {
    return null;
  }
  const zoomLevel = mapRef.current.getZoom();
  //const isCountyZoom = zoomLevel >= ZOOM_LEVELS.COUNTY.MIN;
  const isStateZoom = zoomLevel >= ZOOM_LEVELS.STATE.MIN && zoomLevel < ZOOM_LEVELS.STATE.MAX;

  const { epaRegion, code } = stateToRegionByName[geographicName] || {};

  // Handle county-level zoom
  if (isStateZoom) {
    const countyData = countyViolationsRef?.current?.find(
      county => county.county_name.toLowerCase() === geographicName.toLowerCase()
    );

    // if (countyData) {
      return createPopup({
        title: countyData?.county_name || geographicName,
        subtitle: countyData?.county_code ? `County Code: ${countyData?.county_code}` : '',
        violations: countyData?.violations || 0,
        metrics: {
          population: countyData?.population || 0,
          connections: countyData?.service_connections || 0,
          totalViolations: countyData?.violations || 0
        },
        lngLat: e.lngLat,
        map: mapRef.current
      });
    // }
  }

  // Handle region-level zoom (default)
  if (epaRegion && code) {
    const regionViolations = regionsViolationsRef?.current?.find(
      region => region.epaRegion.toString() === epaRegion.toString()
    )?.violations ?? 0;

    const stateMetrics = waterSystemMapMetricsRef?.current?.find(
      metrics => metrics.stateCode === code
    );

    return createPopup({
      title: geographicName,
      subtitle: `(${code})`,
      region: epaRegion,
      violations: regionViolations,
      metrics: stateMetrics?.metrics,
      lngLat: e.lngLat,
      map: mapRef.current
    });
  }
  return null;
};

export const resetHoverState = () => {
  if (currentPopup) {
    currentPopup.remove();
    currentPopup = null;
  }
}; 